<template>
    <div class="form-group mt-3">
        <label class="headline-section pb-3 pt-3"
            >{{ $t("Email") }}: <b>{{ newEmail }}</b></label
        >
        <input
            type="text"
            name="profile-email"
            autocomplete="off"
            v-model="temporalNewEmail"
            @input="markDirty"
            class="form-control"
            maxlength="255"
            :placeholder="placeholderNewEmail"
        />
    </div>
    <div class="form-group mt-3">
        <label class="headline-section pb-3 pt-3">{{ $t("Enter the password to save the changes") }}</label>
        <input type="password" name="confirm-pass" autocomplete="off" v-model="password" class="form-control" maxlength="255" />
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div class="btn-bottom-modal d-flex justify-content-end align-items-center mt-3">
        <button type="button" :disabled="busy || !dirty" class="btn btn-primary btn-mr" @click="updateEmail">
            <IconItem icon="mdi:content-save" /> {{ $t("Save changes") }}
        </button>
    </div>
</template>

<script lang="ts">
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";
import { getUniqueStringId } from "@/utils/unique-id";
import { AuthController } from "@/control/auth";
import { ApiAccount } from "@/api/api-group-account";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";

export default defineComponent({
    name: "EmailPage",
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            temporalNewEmail: "",
            tfa: AuthController.RequiresTwoFactorAuthentication,
            token: "",
            error: "",
            busy: false,
            password: "",
            placeholderNewEmail: this.$t("New email"),
            newEmail: AuthController.Email,
            dirty: false,
        };
    },
    methods: {
        onAuthChanged: function () {
            this.newEmail = AuthController.Email;
            this.tfa = AuthController.RequiresTwoFactorAuthentication;
            if (!AuthController.isAuthenticated()) {
                this.$requireLogin();
            }
        },

        reset: function () {
            this.error = "";
            this.temporalNewEmail = "";
            this.password = "";
        },

        updateEmail: function () {
            if (this.busy) {
                return;
            }

            this.error = "";
            this.busy = true;

            Request.Pending(
                this.loadRequestId,
                ApiAccount.ChangeEmail({ email: this.temporalNewEmail, password: this.password, tfa_token: this.token }),
            )
                .onSuccess(() => {
                    this.busy = false;
                    this.token = "";
                    this.dirty = false;
                    this.$showSnackBar(this.$t("Email updated successfully"));
                    AuthController.CheckAuthStatus();
                    this.reset();
                })
                .onCancel(() => {
                    this.busy = false;
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    handleErr(err, {
                        badRequestInvalidTfaCode: () => {
                            this.error = this.$t("Invalid one-time password");
                        },
                        badRequestEmailInUse: () => {
                            this.error = this.$t("Invalid email provided");
                        },
                        badRequestEmailInvalid: () => {
                            this.error = this.$t("The provided email is already in use");
                        },
                        badRequestWrongPassword: () => {
                            this.error = this.$t("Wrong password");
                        },
                        badRequestCaptcha: () => {
                            this.error = this.$t("Bad request Captcha");
                        },
                        badRequest: () => {
                            this.error = this.$t("Bad request");
                        },
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.error = err.message;
                    console.error(err);
                    this.busy = false;
                });
        },

        markDirty: function () {
            this.dirty = true;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";
</style>
